import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
    title: string;
    description: string;
    canonical?: string;
    imageUrl?: string;
    ogType?: 'website' | 'article' | 'product';
    schemaData?: Record<string, any> | Record<string, any>[];
    hreflangEn?: string;
    hreflangNl?: string;
    lang?: 'nl' | 'en';
    noindex?: boolean;
}

const SEOHead: React.FC<SEOProps> = ({
    title,
    description,
    canonical,
    imageUrl = 'https://www.kunstlounge.nl/images/collectie/you-carved-out-your-name/you-carved-out-your-name-1.webp',
    ogType = 'website',
    schemaData,
    hreflangEn,
    hreflangNl,
    lang,
    noindex = false
}) => {
    const siteTitle = 'Clemens Briels Originelen | Privécollectie bij Kunstlounge';
    const fullTitle = title === 'Kunstlounge.nl'
        ? siteTitle
        : title.toLowerCase().includes('kunstlounge')
            ? title
            : `${title} | Kunstlounge`;

    const canonicalUrl = canonical ? (canonical.startsWith('http') ? canonical : `https://www.kunstlounge.nl${canonical}`) : 'https://www.kunstlounge.nl/';
    const ogImageUrl = imageUrl.startsWith('http') ? imageUrl : `https://www.kunstlounge.nl${imageUrl}`;

    return (
        <Helmet>
            <html lang={lang || 'nl'} />

            {/* Primary Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="title" content={fullTitle} />
            <meta name="description" content={description} />
            {canonical && <link rel="canonical" href={canonicalUrl} />}

            {/* Hreflang: Bilingual setup */}
            <link rel="alternate" hrefLang="nl" href={hreflangNl ? `https://www.kunstlounge.nl${hreflangNl}` : canonicalUrl} />
            <link rel="alternate" hrefLang="en" href={hreflangEn ? `https://www.kunstlounge.nl${hreflangEn}` : canonicalUrl} />
            <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImageUrl} />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={canonicalUrl} />
            <meta property="twitter:title" content={fullTitle} />
            <meta property="twitter:description" content={description} />
            <meta property="twitter:image" content={ogImageUrl} />

            {/* Robots */}
            <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />

            {/* Additional SEO tags */}
            <meta name="language" content="nl_NL" />
            <meta name="geo.placename" content="Nederland" />
            <meta name="geo.position" content="52.1326;5.2913" />
            <meta name="geo.region" content="NL" />
            <meta name="ICBM" content="52.1326, 5.2913" />

            {/* AEO & GEO Structured Data / JSON-LD */}
            {schemaData && (
                <script type="application/ld+json">
                    {JSON.stringify(schemaData)}
                </script>
            )}
        </Helmet>
    );
};

export default SEOHead;
